/**
 * A subject, as one screen.
 *
 * WHAT THIS REPLACES. `/projects` is a four-level drill-down: project → category
 * → topic → tab → keyword. You walk a corridor to get anywhere, and the most
 * consequential control in the product — the button that turns a keyword into a
 * page — ends up small, on the third tab of the fourth level. The structure was
 * navigation, so everything that acts on the structure had to live underneath it.
 *
 * Here the tree is a RAIL you select in, and the detail changes to match.
 * Selecting the subject root aggregates everything under it; selecting a node
 * scopes to that node. "All keywords in this subject" and "keywords in this
 * node" stop being two screens.
 *
 * LAYOUT IS COPIED FROM BrainstormSession, deliberately — it already solved
 * this, and two of its decisions are load-bearing rather than stylistic:
 *
 *   `minmax(0, 1fr)`, never `1fr`. `1fr` means `minmax(auto, 1fr)`, and `auto`
 *   as a MINIMUM resolves to min-content — so one unbreakable child (a long
 *   keyword, a URL) sets the width of the whole track and pushes past the grid
 *   box. No amount of `min-w-0` on the item fixes it: the overflow is the TRACK.
 *
 *   The wide/narrow decision is a MEDIA QUERY, not a ResizeObserver. An element
 *   cannot be trusted to measure the space available to a layout it is itself
 *   setting — measuring your own box to decide your own width is a feedback loop
 *   that settles on the wrong answer.
 *
 * Lives at /subjects/:projectId, alongside /projects rather than replacing it,
 * so the two can be compared before the drill-down is retired.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Tabs } from '../components/common/Tabs';
import { WORKSPACE_TITLE_CLASS } from '../components/common/PageHeader';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import api from '../services/api';
import { useLiveReload } from '../components/common/useLiveReload';
import Breadcrumb from '../components/Breadcrumb';
import { fetchAllPages } from '../services/paging';
import { useProject } from '../contexts/ProjectContext';
import { useEntitlements } from '../contexts/EntitlementContext';
import { KeywordClipboardProvider } from '../contexts/KeywordClipboardContext';
import { TopicWorkspaceProvider, useTopicWorkspace } from '../contexts/TopicWorkspaceContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import LinkedKeywordsPane from '../components/topic-keywords/LinkedKeywordsPane';
import DiscoveryPane from '../components/topic-keywords/DiscoveryPane';
import KnowledgeDrawer from '../components/subjects/KnowledgeDrawer';
import TopicPagesPane from '../components/topic-keywords/TopicPagesPane';
import DraggableTree from '../components/common/DraggableTree';
import type { TreeMethods } from '../components/common/DraggableTree';
import type { WebSocketEvent } from '../types';
import { collectDescendantIds } from '../components/common/treeHelpers';
import ReturnTrail from '../components/common/ReturnTrail';
import Drawer from '../components/common/Drawer';
// Both were reachable only from the drill-down, and both are SUBJECT-scoped —
// they act on the whole tree, not on whichever node is selected. Retiring that
// screen without carrying them here would have quietly removed two features.
import AgentsPanel from '../components/agents/AgentsPanel';
import StructurePlanPanel from '../components/StructurePlanPanel';
import RoomLink from '../components/subjects/RoomLink';
import SubjectName from '../components/subjects/SubjectName';
import {
  handleEdgeForPaneOn,
  ResizeHandle,
  useResizablePane,
} from '../components/useResizablePane';
import type { Project } from '../types';
import {
  ancestorsOf,
  buildSubjectTree,
  childTopicsOf,
  DRAFT_NODE_ID,
  isDraftNode,
  parseNodeParam,
  renamedName,
  selectionAfterDelete,
  subjectCrumbs,
  wantsPlanner,
  withDraftNode,
} from './subjectTree';
import type { Crumb, SubjectNode } from './subjectTree';

const RAIL_W = 268;
const RAIL_MIN = 200;
const RAIL_MAX = 460;
const DETAIL_MIN = 560;
/** Room for a rail and a usable detail beside it. See the header note on why
 *  this is a media query rather than a measurement. */
const WIDE_MQ = `(min-width: ${RAIL_W + DETAIL_MIN + 1}px)`;
/** Below this the rail, the tabs and the detail are all too short to use, and
 *  "it fits on screen" stops being worth anything. Past it the page scrolls. */
const WORKSPACE_MIN_H = 520;

/**
 * Whether the per-subject Agents panel has a way in.
 *
 * Off: the feature is not ready to be seen. A constant rather than a deleted
 * button so the panel stays compiled and type-checked against the rest of the
 * app — a hidden feature that still builds is one flag from returning; a
 * deleted one is a rewrite.
 */
const SHOW_AGENTS = false;

export type SubjectTab = 'evidence' | 'outputs';
const TABS: { key: SubjectTab; label: string }[] = [
  { key: 'evidence', label: 'Keywords' },
  { key: 'outputs', label: 'Pages' },
];

/**
 * The panes read their scope from ProjectContext / TopicWorkspaceContext rather
 * than from props — that is the seam this workspace plugs into, and it is why
 * they need no changes to move here. The providers wrap the body so the panes
 * and the discovery drawer share one workspace.
 */
export default function SubjectWorkspace() {
  return (
    <KeywordClipboardProvider>
      <TopicWorkspaceProvider>
        <SubjectWorkspaceBody />
      </TopicWorkspaceProvider>
    </KeywordClipboardProvider>
  );
}

function SubjectWorkspaceBody() {
  const { subscribe } = useWebSocket();
  const { regrouping, setRegrouping } = useTopicWorkspace();
  const { projectId: idParam } = useParams<{ projectId: string }>();
  const projectId = Number(idParam);
  const [searchParams, setSearchParams] = useSearchParams();

  const { setSelectedProjectId, setSelectedTopicId, refreshTopics } =
    useProject();
  const [project, setProject] = useState<Project | null>(null);
  const [discoverOpen, setDiscoverOpen] = useState(false);
  const [agentsOpen, setAgentsOpen] = useState(false);
  /* Opened by a deep-link as well as by the button. The page builder's
     "Feeding this build" strip links here to answer "which documents?" — and
     landing on the workspace with the drawer shut answers nothing. */
  const [knowledgeOpen, setKnowledgeOpen] = useState(
    () => searchParams.get('knowledge') !== null,
  );
  /**
   * Open on arrival when the URL asks for it.
   *
   * TWO callers do. `?plan=1` is how "Start a subject" on the Trends page hands
   * you a brand-new subject: the planner is ALREADY thinking about your
   * keywords when you land, and a closed drawer would show you an empty tree
   * and hide the one thing happening — which is the blank-workspace problem
   * that flow exists to fix.
   *
   * `?tab=plan` is the older bookmark, from when Plan was a tab here. The tab
   * whitelist below already stops it blanking the page, but sending it to
   * Evidence with the planner shut still answers the wrong question. Both mean
   * "I came here for the planner", so both open it.
   *
   * Read once, in a lazy initializer, rather than synced to the URL: this is
   * where you ARRIVE, not a mode you are in, and re-deriving it would re-open
   * the drawer every time the param survived a close.
   */
  const [planOpen, setPlanOpen] = useState(() => wantsPlanner(searchParams));
  // The room picker, so the empty subject's first step can open it too.
  const [roomPickerOpen, setRoomPickerOpen] = useState(false);
  const { has } = useEntitlements();
  const [nodes, setNodes] = useState<SubjectNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [railOpen, setRailOpen] = useState(false);
  /**
   * Creating nodes from the rail, so the workspace stops needing the old
   * drill-down to do half a job. A node goes under whatever is selected — or at
   * the top of the subject when nothing is, which is what a section used to be.
   */
  // `undefined` means no draft is open; `null` means one is open at the ROOT.
  // Two different states, and collapsing them would make "add a section" and
  // "not adding anything" indistinguishable.
  const [draftParentId, setDraftParentId] = useState<number | null | undefined>(
    undefined,
  );
  const [draftName, setDraftName] = useState('');
  /**
   * Renaming happens ON THE ROW, in the same shaped box the draft uses.
   *
   * A node's name was editable in exactly one place — the old topic settings
   * page, which nothing in the app links to — so in practice a section you
   * misnamed had to be deleted and rebuilt, taking its keywords and pages with
   * it. Here it is the third verb beside + and the bin, and a double-click on
   * the name does the same thing.
   *
   * `null` means nothing is being renamed. The name lives beside the id so the
   * field is uncontrolled by the tree data — typing must not depend on a
   * refetch landing.
   */
  const [renameId, setRenameId] = useState<number | null>(null);
  const [renameName, setRenameName] = useState('');
  // The tree library owns open/closed state, so expand-all is an imperative
  // call rather than a prop — there is nothing on this side to set.
  const treeRef = useRef<TreeMethods | null>(null);

  // Validated, not cast. `?tab=plan` is a bookmark people have — Plan was a
  // tab here until Link Builder replaced it — and an unchecked cast rendered
  // NOTHING for it: no tab highlighted, no pane, no error. A whitelist sends
  // any unknown value to the default instead.
  const rawTab = searchParams.get('tab');
  const tab: SubjectTab = TABS.some((t) => t.key === rawTab)
    ? (rawTab as SubjectTab)
    : 'evidence';
  const selected = parseNodeParam(searchParams.get('node'), nodes);

  const setParam = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(searchParams);
      if (value) next.set(key, value);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /** Where a crumb points.
   *
   *  Built off the CURRENT query string, so the tab survives the move — clicking an
   *  ancestor while reading Plan should not drop you back on Evidence.
   *
   *  A real href rather than an onClick: these are the only navigation on the screen
   *  that leaves where you are, and a link can be middle-clicked, copied, and read in
   *  the status bar before you commit to it. They PUSH history, unlike selecting a node
   *  in the rail (which replaces) — clicking your way up a path and then going Back
   *  should return you down it.
   */
  const crumbHref = useCallback(
    (crumb: Crumb) => {
      if (crumb.target.kind === 'list') return '/subjects';
      const next = new URLSearchParams(searchParams);
      if (crumb.target.id === null) next.delete('node');
      else next.set('node', String(crumb.target.id));
      const qs = next.toString();
      return `/subjects/${projectId}${qs ? `?${qs}` : ''}`;
    },
    [searchParams, projectId],
  );

  const createNode = useCallback(
    async (name: string, under: SubjectNode | null) => {
      // parent_id reaches ANY node; the old category_id could only ever address
      // a root, which is why the drill-down could not build this tree.
      const created = await api.createTopic(
        under
          ? { name, parent_id: under.id }
          : { name, project_id: projectId },
      );
      return created;
    },
    [projectId],
  );

  /**
   * Clear the run flag when the job lands, and say so while it is going.
   *
   * The listener is HERE, not in the pane that started it: only the active tab
   * is mounted, so pressing the button on Plan and switching to Evidence
   * unmounts the only thing that was waiting. The workspace outlives every
   * tab, so it is the one place that can both hold the flag and hear the end
   * of the run.
   */
  useEffect(() => {
    return subscribe((event: WebSocketEvent) => {
      if (event.event_type !== 'terms_regrouped') return;
      setRegrouping(false);
      if (event.status !== 'done') {
        setError(event.message || 'The regroup did not finish.');
      }
    });
  }, [subscribe, setRegrouping]);

  const load = useCallback(async (quiet = false) => {
    if (!Number.isFinite(projectId)) return;
    if (!quiet) setLoading(true);
    try {
      // Both list endpoints declare `limit: le=100` — asking for more is a 422,
      // not a clamp, so the rail rendered an error where the tree should be.
      // Paged rather than capped at 100: a subject with 140 topics would
      // otherwise draw 100 of them in a tree with nothing saying the rest exist.
      // One fetch for the tree. A root is a topic with no parent since
      // migration 108, so /topics returns the whole subject — the separate
      // /categories call was fetching rows this list already contains.
      const [proj, tops] = await Promise.all([
        api.getProject(projectId),
        fetchAllPages((skip, limit) => api.getTopics(null, projectId, skip, limit)),
      ]);
      setProject(proj);
      setNodes(buildSubjectTree(tops));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load this subject');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // AFTER A CHANGE, the shared topic list has to move too. The Keywords and
  // Pages tabs find the selected node in that list (ProjectContext), which is
  // loaded once per subject - so a section made by Apply or by hand was
  // missing from it, and its tabs said "Select a topic" until a browser
  // reload. The first load above does not need this: the context fetches its
  // own list when the subject changes.
  const reload = useCallback(
    async (quiet = false) => {
      await Promise.all([load(quiet), refreshTopics()]);
    },
    [load, refreshTopics],
  );
  // A GROWTH SESSION CAN ADD CATEGORIES AND TOPICS in the background; the tree
  // showed them only after a reload. Re-read, quietly, when one ends.
  useLiveReload(
    (e) => e?.event_type === 'growth_agent_llm' && e.project_id === projectId && ['session_complete', 'failed'].includes(e.status),
    () => void reload(true),
  );

  // Wide/narrow: a media query, never a measurement. See the header note.
  const [wide, setWide] = useState(
    () => window.matchMedia?.(WIDE_MQ).matches ?? true,
  );
  useEffect(() => {
    const mq = window.matchMedia(WIDE_MQ);
    const onChange = () => setWide(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const rail = useResizablePane({
    storageKey: 'jcseo_subject_rail',
    initial: RAIL_W,
    min: RAIL_MIN,
    max: RAIL_MAX,
    // The resized pane is the RAIL, on the left; its handle is on its right edge.
    // The grid is "rail handle detail", so the handle is on the rail's RIGHT.
    // Copied from BrainstormSession, where the same hook drives a handle on the
    // LEFT of a right-hand pane — so 'left' was right there and inverted here:
    // dragging left made the rail wider and the divider ran the other way.
    handleOn: handleEdgeForPaneOn('left'),
  });

  const chromeRef = useRef<HTMLDivElement>(null);
  const [chromeHeight, setChromeHeight] = useState(0);
  useEffect(() => {
    const el = chromeRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => setChromeHeight(el.offsetHeight));
    ro.observe(el);
    setChromeHeight(el.offsetHeight);
    return () => ro.disconnect();
  }, []);

  const visibleNodes = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return nodes;
    // Keep a matching node's ancestors, or the match is unreachable in a tree.
    const byId = new Map(nodes.map((n) => [n.id, n]));
    const keep = new Set<number>();
    for (const n of nodes) {
      if (!n.name.toLowerCase().includes(q)) continue;
      let cur: SubjectNode | undefined = n;
      // Guarded: a cycle in the parent chain would otherwise spin here.
      while (cur && !keep.has(cur.id)) {
        keep.add(cur.id);
        cur = cur.parentId != null ? byId.get(cur.parentId) : undefined;
      }
    }
    return nodes.filter((n) => keep.has(n.id));
  }, [nodes, filter]);

  /**
   * The rows the rail draws, including the draft while one is open.
   *
   * The draft is inserted AFTER filtering: it has no name yet, so any active
   * filter would remove the row the user is typing into.
   */
  const railNodes = useMemo(
    () =>
      draftParentId === undefined
        ? visibleNodes
        : withDraftNode(visibleNodes, draftParentId),
    [visibleNodes, draftParentId],
  );


  // The panes read their scope from ProjectContext, so the rail's selection has
  // to land there. Project is set on its own and only when it changes, because
  // setSelectedProjectId CASCADES — it clears category and topic — and running
  // it alongside the node effect would wipe the selection it just made.
  useEffect(() => {
    if (Number.isFinite(projectId)) setSelectedProjectId(projectId);
  }, [projectId, setSelectedProjectId]);

  const selectedNode = useMemo(
    () => (selected == null ? null : nodes.find((n) => n.id === selected) ?? null),
    [nodes, selected],
  );

  // Both the header path and the rail's open state come from this — the header
  // was showing only the subject, so there was no way to tell which section a
  // topic was in, and the rail arrived collapsed with the selected node hidden.
  const ancestors = useMemo(
    () => ancestorsOf(nodes, selectedNode),
    [nodes, selectedNode],
  );
  const crumbs = useMemo(
    () => subjectCrumbs(project?.name, ancestors, selectedNode),
    [project?.name, ancestors, selectedNode],
  );
  const openIds = useMemo(
    () => (selectedNode ? [...ancestors.map((a) => a.id), selectedNode.id] : undefined),
    [ancestors, selectedNode],
  );

  const childTopics = useMemo(
    () => childTopicsOf(nodes, selectedNode),
    [nodes, selectedNode],
  );

  // The panes still read a topic id from ProjectContext, so the rail's
  // selection lands there. Every node is a topic now, so there is no branch:
  // a root selects as itself rather than as "a category with no topic".
  useEffect(() => {
    setSelectedTopicId(selectedNode ? selectedNode.id : null);
  }, [selectedNode, setSelectedTopicId]);

  /**
   * Any node under any node.
   *
   * This used to consult `canReparent`, which refused most drags — a topic
   * could not sit at the root and a section could not be nested, because the
   * model had two levels and no column to express anything else. It has one
   * level-free column now, so the only illegal move is into your own subtree,
   * and the tree component already blocks that.
   */
  /**
   * Delete the selected node and everything under it.
   *
   * The self-FK cascades, so this is never one row: a section takes its
   * children, their keywords and their pages. The count comes from the tree
   * already loaded here, so the warning names a real number rather than a
   * vague "and its contents".
   */
  const deleteNode = useCallback(
    async (node: SubjectNode) => {
      const descendants = collectDescendantIds(
        nodes,
        node.id,
        (n) => n.id,
        (n) => n.parentId,
      );
      const extra = descendants.size;
      const warning =
        `Delete "${node.name}"?\n\n` +
        (extra > 0
          ? `${extra} node${extra === 1 ? '' : 's'} beneath it will be deleted too, ` +
            'along with the keywords and pages on all of them.\n\n'
          : 'Its keywords and pages will be deleted too.\n\n') +
        'This cannot be undone.';
      if (!window.confirm(warning)) return;
      setError(null);
      try {
        await api.deleteTopic(node.id);
        // Only move if the ground you were standing on went with it — see
        // `selectionAfterDelete`. Deleting a branch elsewhere in the tree while you
        // read this one must leave you where you are.
        const next = selectionAfterDelete(nodes, node, selected);
        if (next !== undefined) setParam('node', next === null ? null : String(next));
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not delete that node');
      }
    },
    [nodes, selected, setParam, reload],
  );

  /** Add a child UNDER a given row: select it, then open the one add box there is.
   *
   *  Reusing that box rather than growing a second create path is the point — it
   *  already names its parent, saves on Enter, and puts you on the new node when it
   *  lands. Selecting first is also what opens the row, so the child appears where you
   *  asked for it instead of inside a collapsed branch. */
  /**
   * Start naming a new node IN PLACE, under the row you pressed + on.
   *
   * A draft row appears where the node is going to live, rather than a field
   * opening at the top of the rail. Two things follow from that: you can see
   * the shape you are making while you type, and your eye does not have to
   * travel away from the row you just acted on and back again.
   *
   * It does NOT change the selection. Pressing + on a row you were not working
   * in should not throw away the pane you are looking at — that was a side
   * effect of the old flow, which had to select the row so the form knew where
   * to put the node.
   */
  const startAddUnder = useCallback((node: SubjectNode | null) => {
    setDraftParentId(node ? node.id : null);
    setDraftName('');
    // Opened, or the draft is a row inside a collapsed subtree and the focus
    // lands somewhere the user cannot see.
    if (node) treeRef.current?.open([node.id]);
  }, []);

  const cancelDraft = useCallback(() => {
    setDraftParentId(undefined);
    setDraftName('');
  }, []);

  const commitDraft = useCallback(async () => {
    const name = draftName.trim();
    const parentId = draftParentId;
    if (!name || parentId === undefined) {
      cancelDraft();
      return;
    }
    cancelDraft();
    try {
      const under = parentId === null ? null : nodes.find((n) => n.id === parentId) ?? null;
      const created = await createNode(name, under);
      await reload();
      setParam('node', String(created.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not add that node');
    }
  }, [draftName, draftParentId, nodes, createNode, reload, setParam, cancelDraft]);

  /** Open the rename box on a row, closing any draft — two live name fields in
   *  one rail is two things claiming the focus. */
  const startRename = useCallback(
    (node: SubjectNode) => {
      cancelDraft();
      setRenameId(node.id);
      setRenameName(node.name);
    },
    [cancelDraft],
  );

  const cancelRename = useCallback(() => {
    setRenameId(null);
    setRenameName('');
  }, []);

  /**
   * Save the new name, or quietly stop if there is nothing to save.
   *
   * An empty box means "no" rather than "call this node nothing": a blank name
   * would render an unclickable row. Reloading afterwards rather than patching
   * `nodes` in place keeps the header, the trail and the pick lists reading the
   * one source they already read.
   */
  const commitRename = useCallback(async () => {
    const id = renameId;
    if (id === null) return;
    const name = renamedName(nodes, id, renameName);
    cancelRename();
    if (name === null) return;
    setError(null);
    try {
      await api.updateTopic(id, { name });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not rename that node');
    }
  }, [renameId, renameName, nodes, cancelRename, reload]);

  const handleReparent = useCallback(
    async (id: number, newParentId: number | null) => {
      // The draft has no row on the server. Dragging it, or onto it, would PUT
      // a negative id — a 404 at best, and at worst it would move a real node
      // under something that does not exist.
      if (id === DRAFT_NODE_ID || newParentId === DRAFT_NODE_ID) return;
      setError(null);
      try {
        await api.updateTopic(id, { parent_id: newParentId, project_id: projectId });
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not move that node');
      }
    },
    [reload, projectId],
  );

  const railBody = (
    <div className="flex flex-col min-h-0 h-full">
      <div className="px-4 pt-4 pb-3">
        {/* Was the dead word "SUBJECT" — a caption naming the kind of thing below it.
            It is the first thing the eye lands on at wide widths, so it costs nothing
            to make it the way out instead. (The centre trail carries the same exit,
            because this rail is not rendered at all on a narrow screen — it becomes a
            drawer behind the Nodes button, which is exactly when you are most lost.) */}
        <Link
          to="/subjects"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
        >
          <span aria-hidden="true">‹</span> Subjects
        </Link>
        {project ? (
          <SubjectName
            project={project}
            onRenamed={(saved) => setProject((prev) => (prev ? { ...prev, ...saved } : saved))}
          />
        ) : (
          <div className="text-[17px] font-semibold text-gray-900 dark:text-gray-100">…</div>
        )}
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter sections…"
          aria-label="Filter sections"
          className="mt-3 block w-full px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
        />
      </div>


      {/* Only worth offering when the tree HAS depth. A flat list has nothing
          to expand, and a control that does nothing teaches people the bar is
          decorative. */}
      {nodes.some((n) => n.parentId !== null) && (
        <div className="px-4 pb-2 flex items-center justify-end">
          {/* Right-aligned, over the chevrons they drive. The pair is the
              bulk version of the per-row control, so they line up with it
              rather than sitting at the opposite edge of the rail. */}
          <div className="inline-flex">
            <button
              type="button"
              onClick={() => treeRef.current?.openAll()}
              className="px-2 py-0.5 text-[11px] font-medium rounded-l border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Expand all
            </button>
            <button
              type="button"
              onClick={() => treeRef.current?.closeAll()}
              className="px-2 py-0.5 text-[11px] font-medium rounded-r border border-l-0 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              Collapse
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {/* A VERB, where a destination used to be.
            This row was "Everything in this subject" — the way back to the
            whole-subject view — with a + hung on it. The trail carries that
            destination already (the subject's name is a crumb to it the moment
            any node is selected), so the row was a second door to one place
            and the only door to another. It is the verb now.

            Kept at the TOP of the tree because that is where a section is
            about to appear: the draft opens directly beneath it. */}
        <button
          type="button"
          onClick={() => startAddUnder(null)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 mb-1 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 dark:focus:ring-offset-gray-800"
        >
          <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
          </svg>
          New section
        </button>

        {visibleNodes.length === 0 && !loading && (
          <p className="px-2 py-3 text-sm text-gray-400 dark:text-gray-500">
            {filter ? 'Nothing matches that filter.' : 'No sections yet.'}
          </p>
        )}

        <DraggableTree<SubjectNode>
          onReady={(m) => {
            treeRef.current = m;
          }}
          items={railNodes}
          getId={(n) => n.id}
          getParentId={(n) => n.parentId}
          getLabel={(n) => n.name}
          onReparent={handleReparent}
          onError={setError}
          openIds={openIds}
          renderRow={(node, { depth, isOpen, hasChild, onToggle, handleRef }) => {
            const isSelected =
              selected === node.id;
            if (isDraftNode(node)) {
              return (
                <div
                  className="flex items-center gap-1 px-2 py-1 rounded-md"
                  style={{ paddingLeft: `${8 + depth * 14}px` }}
                >
                  {/* Says where this is going, which the row's position alone
                      cannot at depth 0 — a new section sits directly under the
                      subject row and would otherwise look like a sibling of
                      it. A caret rather than words: it is one glyph wide and
                      the field beside it is what the eye is on. */}
                  <span
                    className="shrink-0 text-blue-400 dark:text-blue-500 text-xs"
                    aria-hidden="true"
                  >
                    ↳
                  </span>
                  <input
                    autoFocus
                    type="text"
                    value={draftName}
                    aria-label="New section name"
                    placeholder="Name it"
                    onChange={(e) => setDraftName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelDraft();
                      if (e.key === 'Enter') commitDraft();
                    }}
                    // Saves on blur rather than discarding. A name typed and
                    // then abandoned by clicking elsewhere is work; Escape is
                    // the way to mean "no". Matches the label chips, which
                    // also persist without a Save button.
                    onBlur={commitDraft}
                    className="flex-1 min-w-0 px-1.5 py-0.5 text-sm bg-white dark:bg-gray-700 border border-blue-400 dark:border-blue-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:text-gray-100"
                  />
                </div>
              );
            }

            return (
              <div
                ref={handleRef}
                onClick={() => setParam('node', String(node.id))}
                style={{ paddingLeft: depth * 16 + 8 }}
                className={`group flex items-center gap-1.5 pr-2 py-1.5 rounded-md text-sm cursor-pointer ${
                  isSelected
                    ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200 font-medium'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {renameId === node.id ? (
                  <input
                    autoFocus
                    type="text"
                    value={renameName}
                    aria-label={`Rename ${node.name}`}
                    onChange={(e) => setRenameName(e.target.value)}
                    // The row is both a drag handle and the selector, so the
                    // press and the click both have to stop here — otherwise
                    // putting the caret in the middle of the word starts a
                    // drag, or re-selects the row out from under the field.
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') cancelRename();
                      if (e.key === 'Enter') commitRename();
                    }}
                    // Same rule as the draft box and the label chips: a name
                    // typed and then clicked away from is work, Escape is how
                    // you mean no.
                    onBlur={commitRename}
                    className="flex-1 min-w-0 px-1.5 py-0.5 text-sm bg-white dark:bg-gray-700 border border-blue-400 dark:border-blue-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 dark:text-gray-100"
                  />
                ) : (
                  /* Double-click renames as well as the pencil does — it is
                     what the gesture means everywhere else a name sits in a
                     tree, and it costs nothing to honour. */
                  <span
                    className="flex-1 truncate"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      startRename(node);
                    }}
                  >
                    {node.name}
                  </span>
                )}

                {/* WHAT YOU CAN DO TO THIS NODE, on this node.
                    Adding a child meant selecting the row, then travelling to the top
                    of the rail to press "+ New node under X"; deleting meant selecting
                    it, then crossing to the other pane's header. Both verbs are ABOUT a
                    row, so they belong on it.

                    Faint rather than hidden. Invisible-until-hover is tidier on a long
                    tree, but nothing then tells you the verbs exist — you have to sweep
                    the rail to find out. Held at a weight that reads as texture until
                    the row is under the pointer, they are discoverable without
                    competing with the names, which are what the rail is for.

                    `shrink-0` and always mounted: they hold their width whether or not
                    the pointer is here, so a name never reflows as you sweep down. */}
                <span className="flex shrink-0 items-center gap-0.5">
                  {/* On the RIGHT, with the verbs. It used to sit left of the
                      name, where it indented every row by its width whether or
                      not the row had children — so the names did not line up
                      and the eye had to find the start of each one.
                      Full strength, unlike the verbs beside it: expanding is
                      not a destructive act and it is the one you reach for
                      most, so it should not need a hover to be legible. */}
                  <button
                    type="button"
                    aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggle();
                    }}
                    className={`p-0.5 rounded text-gray-400 dark:text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600 ${
                      hasChild ? '' : 'invisible pointer-events-none'
                    }`}
                  >
                    <svg
                      className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2.5}
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>

                  <span className="flex items-center gap-0.5 opacity-40 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    aria-label={`Rename ${node.name}`}
                    title={`Rename "${node.name}"`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      startRename(node);
                    }}
                    className="p-0.5 rounded text-gray-400 dark:text-gray-500 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label={`Add a section under ${node.name}`}
                    title={`Add a section under ${node.name}`}
                    // The row is a drag handle, so the press has to be stopped from
                    // starting a drag as well as from selecting.
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      startAddUnder(node);
                    }}
                    className="p-0.5 rounded text-gray-400 dark:text-gray-500 hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:text-blue-700 dark:hover:text-blue-300"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    aria-label={`Delete ${node.name}`}
                    title={`Delete "${node.name}" and everything under it`}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteNode(node);
                    }}
                    className="p-0.5 rounded text-gray-400 dark:text-gray-500 hover:bg-red-100 dark:hover:bg-red-900/50 hover:text-red-700 dark:hover:text-red-300"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                  </span>
                </span>
              </div>
            );
          }}
        />
      </div>
    </div>
  );

  if (!Number.isFinite(projectId)) {
    return <p className="p-8 text-red-500">Subject not found</p>;
  }

  return (
    <div className="flex flex-col min-h-0">
      <div ref={chromeRef}>
        {/* Arrived from a page build to check what feeds it. Opening a drawer
            here kills browser-back, which is precisely what you came to do. */}
        {error && (
          <div className="mx-4 mt-3 text-sm text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded p-2">
            {error}
          </div>
        )}
      </div>

      <div
        className="grid min-h-0 min-w-0"
        style={{
          height: `max(${WORKSPACE_MIN_H}px, calc(100vh - 5rem - ${chromeHeight}px))`,
          // minmax(0, 1fr) — see the header note. A long keyword in the detail
          // would otherwise set the width of the whole column.
          gridTemplateColumns: wide
            ? `${rail.width}px 1px minmax(0, 1fr)`
            : 'minmax(0, 1fr)',
          gridTemplateAreas: wide ? '"rail handle detail"' : '"detail"',
        }}
      >
        {wide && (
          <>
            <div
              style={{ gridArea: 'rail' }}
              className="min-h-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
            >
              {railBody}
            </div>
            <ResizeHandle
              onPointerDown={rail.onPointerDown}
              dragging={rail.dragging}
              axis="x"
              title="Drag to resize"
              style={{ gridArea: 'handle' }}
            />
          </>
        )}

        <div style={{ gridArea: 'detail' }} className="min-h-0 min-w-0 flex flex-col">
          {/* Title row, then the tabs on their own row beneath it. The tabs used
              to sit at the right end of the title row, where they read as two
              more buttons beside Link a room / Knowledge / Plan structure. */}
          <div
            className={`px-6 pt-4 border-b border-gray-200 dark:border-gray-700 ${
              selectedNode ? '' : 'pb-3'
            }`}
          >
          <div className="flex items-end gap-4">
            {!wide && (
              <button
                onClick={() => setRailOpen(true)}
                className="mb-2 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300"
              >
                Outline
              </button>
            )}
            <div className="min-w-0 flex-1">
              {/* THE WAY BACK OUT. This trail used to start at the subject's name and
                  be plain text, which left the workspace with no exit at all: the top
                  nav's Subjects tab is sticky, so it returns you to the subject you are
                  already in. Back and the address bar were the only ways to the list.

                  Starting at `Subjects` also makes the row read as a path rather than a
                  label — the leading name only looked like a caption because nothing
                  above it was named. */}
              {/* The shared trail. This was a hand-rolled `›`-separated copy at
                  a smaller size — same idea as the one every other screen uses,
                  rendered differently, so the trail changed shape as you moved
                  between screens. `crumbHref` still builds the targets, so the
                  query string still survives the move. */}
              <Breadcrumb
                className="mb-0"
                // The way out, right end of the breadcrumb row — the one
                // placement every screen uses. It was floated above this with
                // its own margins, which in a full-height editor landed at a
                // different x-offset from every other screen.
                right={<ReturnTrail className="mb-0" />}
                items={crumbs.map((crumb) => ({
                  label: crumb.label,
                  to: crumbHref(crumb),
                  // Every crumb here is an ANCESTOR — `subjectCrumbs` leaves the
                  // selected node out, because the <h1> below is that node. So
                  // none of them is the current page, including the last.
                  isCurrent: false,
                }))}
              />
              <div className="flex items-center gap-3 mb-1">
                <h1 className={`flex items-center gap-2 min-w-0 ${WORKSPACE_TITLE_CLASS}`}>
                <span className="truncate">
                  {selectedNode?.name ?? 'Everything in this subject'}
                </span>
                {/* Visible from every tab, because the run outlives the tab
                    that started it: the data under you changes when it lands,
                    and without this the only sign was the numbers moving. */}
                {regrouping && (
                  <span
                    className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 shrink-0"
                    title="Grouping near-duplicate terms onto shared pages. This list refreshes when it finishes."
                  >
                    <svg
                      className="w-3 h-3 animate-spin"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                      strokeLinecap="round"
                    >
                      <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
                    </svg>
                    Regrouping
                  </span>
                )}
              </h1>
              {/* ON THE TITLE LINE, because they act on the SUBJECT — the tabs
                  below scope to the selected node, and mixing the two scopes in
                  one row said they were the same kind of thing. They are not:
                  a tab says where you are, these do something. The empty state
                  made it plain — with no node selected the tabs are inert and
                  these two still work.

                  Quiet, too. They were the same weight as the tabs while being
                  used far less often, and the loudest thing on the screen
                  should be the subject you are in. */}
              <div className="ml-auto flex shrink-0 items-center gap-1">
                {/* WHICH ROOM THIS SUBJECT READS, chosen here rather than from
                    the room: this is the side that asks the question, and the
                    same room can feed several subjects.

                    Here and not in project settings, which nothing in the app
                    links to — a control reachable only by typing a URL is the
                    state this link was already in once, and the reason it went
                    unused. */}
                {project && (
                  <RoomLink
                    projectId={project.id}
                    boardroomId={project.boardroom_id ?? null}
                    onChange={(boardroom_id) =>
                      setProject((prev) =>
                        prev ? { ...prev, boardroom_id } : prev,
                      )
                    }
                    open={roomPickerOpen}
                    onOpenChange={setRoomPickerOpen}
                  />
                )}
                {/* Beside Plan structure because both act on what you have
                    SELECTED, and both open over the workspace rather than
                    replacing it. The panels behind this existed and were
                    reachable only by typing a legacy settings URL. */}
                <button
                  onClick={() => setKnowledgeOpen(true)}
                  className="px-2 py-1 text-xs rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
                  title={
                    selectedNode
                      ? `What the agent should know about ${selectedNode.name}`
                      : 'What the agent should know about this subject'
                  }
                >
                  Knowledge
                </button>
                <button
                  onClick={() => setPlanOpen(true)}
                  className="px-2 py-1 text-xs rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Plan structure
                </button>
                {/* HIDDEN, not deleted. The panel and its state below are
                    still wired up and still compile, so putting the entry point
                    back is this one flag — whereas deleting the button would
                    bit-rot the panel behind it, and the next person to want it
                    would find code nothing has exercised in months.

                    Set to true when the feature is ready to be seen. */}
                {SHOW_AGENTS && (
                  <button
                    onClick={() => setAgentsOpen(true)}
                    className="px-2 py-1 text-xs rounded text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Agents
                  </button>
                )}
                </div>
              </div>
            </div>
          </div>
            {/* Only with a section selected: the tabs show ONE section's
                keywords or pages, and with none selected they did nothing. */}
            {selectedNode && (
              <Tabs
                tabs={TABS}
                active={tab}
                onChange={(key) => setParam('tab', key)}
                // -mb-px lays the tabs' rule over the header's, so they
                // draw as one line.
                className="mt-2 -mb-px"
              />
            )}
          </div>

          {/* The panes are per-TOPIC today — they read selectedTopic. Until the
              Node migration gives every node its own keywords, selecting a
              section or the root shows what is under it instead of an empty
              pane pretending to be a view. */}
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            {loading ? (
              <p className="px-6 py-5 text-sm text-gray-500 dark:text-gray-400">Loading…</p>
            ) : selectedNode ? (
              <div className="flex-1 min-h-0 overflow-hidden">
                {tab === 'evidence' && (
                  <LinkedKeywordsPane onDiscoverClick={() => setDiscoverOpen(true)} />
                )}
                {tab === 'outputs' && <TopicPagesPane />}
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto px-6 py-5">
                {/* "Pick a node" is wrong advice when there is nothing to
                    pick — an empty subject was telling people to choose from
                    an empty rail. An empty subject gets the steps instead. */}
                {nodes.length === 0 ? (
                  <EmptySubjectSteps
                    showRoomStep={!!project && (!!project.boardroom_id || has('BOARDROOM'))}
                    roomLinked={!!project?.boardroom_id}
                    onLinkRoom={() => setRoomPickerOpen(true)}
                    onPlan={() => setPlanOpen(true)}
                    onAddSection={() => {
                      // On narrow screens the outline is a drawer, and the new
                      // section's name box lives in it.
                      if (!wide) setRailOpen(true);
                      startAddUnder(null);
                    }}
                  />
                ) : (
                  <>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      This subject's outline. Pick a section to see its keywords and pages.
                    </p>
                    <ul className="mt-4 flex flex-col gap-1">
                      {childTopics.map((t) => (
                        <li key={t.id}>
                          <button
                            onClick={() => setParam('node', String(t.id))}
                            className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            {t.name}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {agentsOpen && (
        <Drawer
          onClose={() => setAgentsOpen(false)}
          side="right"
          width="w-[42rem]"
          eyebrow="Subject"
          title={`Agents · ${project?.name ?? ''}`}
        >
          <AgentsPanel
            scope="project"
            scopeId={projectId}
            scopeName={project?.name ?? ''}
          />
        </Drawer>
      )}

      {knowledgeOpen && (
        <KnowledgeDrawer
          onClose={() => {
            setKnowledgeOpen(false);
            const next = new URLSearchParams(searchParams);
            next.delete('knowledge');
            setSearchParams(next, { replace: true });
          }}
          projectId={projectId}
          initialTab={
            searchParams.get('knowledge') === 'documents' ? 'documents' : 'knowledge'
          }
          // A node with no parent is what a category was before migration 108,
          // and the knowledge API still addresses it by that name. Passing
          // "topic" for a root would create a SECOND brief keyed the other way,
          // so the two screens would each show knowledge the other could not.
          scope={
            selectedNode
              ? selectedNode.parentId === null
                ? 'category'
                : 'topic'
              : 'project'
          }
          scopeId={selectedNode ? selectedNode.id : projectId}
          scopeName={selectedNode?.name ?? project?.name ?? 'this subject'}
          scopePath={
            selectedNode
              ? [project?.name, ...ancestors.map((a) => a.name)]
                  .filter(Boolean)
                  .join(' › ')
              : 'Subject'
          }
        />
      )}

      {planOpen && (
        <Drawer
          onClose={() => {
            setPlanOpen(false);
            // Drop the arrival params, or a reload re-opens the drawer that was
            // just dismissed.
            const next = new URLSearchParams(searchParams);
            next.delete('plan');
            if (next.get('tab') === 'plan') next.delete('tab');
            setSearchParams(next, { replace: true });
            // Apply reloads as it runs (onApplied); this catches anything
            // else that changed while the drawer was open.
            reload();
          }}
          side="right"
          width="w-[52rem]"
          eyebrow="Subject"
          title={`Plan structure · ${project?.name ?? ''}`}
        >
          <StructurePlanPanel projectId={projectId} onApplied={() => reload(true)} />
        </Drawer>
      )}

      {discoverOpen && (
        <Drawer
          onClose={() => setDiscoverOpen(false)}
          side="right"
          width="w-[46rem]"
          eyebrow="Research"
          title={`Discover keywords · ${selectedNode?.name ?? ''}`}
          // The idea list and the chat scroll SEPARATELY. Without this the panel
          // scrolls as one, so the chat's prompt box lands below however many
          // keywords the left column happens to be showing.
          fill
        >
          <DiscoveryPane />
        </Drawer>
      )}

      {!wide && railOpen && (
        <Drawer
          onClose={() => setRailOpen(false)}
          side="left"
          width="w-[20rem]"
          eyebrow="Subject"
          title={project?.name ?? 'Outline'}
        >
          {railBody}
        </Drawer>
      )}
    </div>
  );
}

/**
 * What an empty subject shows: the steps to get started, each with its button.
 * The same actions sit on the title row, but there they are small and say
 * nothing about order.
 */
function EmptySubjectSteps({
  showRoomStep,
  roomLinked,
  onLinkRoom,
  onPlan,
  onAddSection,
}: {
  /** Hidden for accounts without Boardroom, like the Link a room button. */
  showRoomStep: boolean;
  roomLinked: boolean;
  onLinkRoom: () => void;
  onPlan: () => void;
  onAddSection: () => void;
}) {
  const stepButton =
    'shrink-0 px-3 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700';
  const steps = [
    showRoomStep && (
      <li key="room" className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Link a room <span className="font-normal text-gray-500 dark:text-gray-400">(optional)</span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Gives the planner and the writer research to work from.
          </p>
        </div>
        {roomLinked ? (
          <span className="shrink-0 text-sm text-green-700 dark:text-green-400">Linked ✓</span>
        ) : (
          <button onClick={onLinkRoom} className={stepButton}>
            Link a room
          </button>
        )}
      </li>
    ),
    <li key="plan" className="flex items-start justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">Plan structure</div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Suggests sections for this subject. You review them before anything is added.
        </p>
      </div>
      <button
        onClick={onPlan}
        className="shrink-0 px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
      >
        Plan structure
      </button>
    </li>,
  ].filter(Boolean);

  return (
    <div className="max-w-xl">
      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
        This subject has no sections yet
      </h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        Sections hold the keywords and pages. To get started:
      </p>
      <ol className="mt-4 space-y-4">{steps}</ol>
      <p className="mt-5 text-sm text-gray-500 dark:text-gray-400">
        Or{' '}
        <button onClick={onAddSection} className="text-blue-600 dark:text-blue-400 hover:underline">
          add a section by hand
        </button>
        , or send keywords here from Trending now.
      </p>
    </div>
  );
}
